import * as React from 'react';
import { EmployeeToken, permissionGroupHelper, UserToken } from '../../amorg-common';
import { getMaybeActiveEmployeeOrLogin } from '../../api/serverApiHelpers';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { EmployeePortalPageProvider } from '../_contexts/EmployeePortalPageContext';
import { LinkButton } from '../../designSystem/button/LinkButton';
import { Footer } from '../../designSystem/layouts/pageWrappers/elements/Footer';
import styled from 'styled-components';
import { desktop } from '../../designSystem/BreakPoints';
import { applicationEndpoints } from '../../amorg-common/applicationEndpoints';
import { clientApi } from '../../api/clientApi';
import { P } from '../../designSystem/typography/Body';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { PageHeader } from '../../designSystem/PageHeader';

type EmployeePageWrapperProps = {
    disablePortalStatusCheck?: boolean,
    children: React.ReactNode;
};

export const EmployeePageWrapper: React.FunctionComponent<EmployeePageWrapperProps> = (props: EmployeePageWrapperProps) => 
{
    const [activeUser, setActiveUser] = React.useState<UserToken | EmployeeToken | null>(null);
    const [portalDisabed, setPortalDisabled] = React.useState(false);

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const result = await getMaybeActiveEmployeeOrLogin();
            setActiveUser(result);
            if (!props.disablePortalStatusCheck)
            {
                const options = await clientApi.portal.getLoginOptions(result.siteId);
                setPortalDisabled(!options.enabled);
            }
        };
        getData();
    }, []);

    const allowed = activeUser !== null && !portalDisabed;

    return (
        <EmployeePortalPageProvider user={activeUser}>
            <Main>
                <PublicNavigation>
                    <Wrapper>
                        <PublicLogo>
                            <img src="/images/amorg_logo_blue.svg" />
                        </PublicLogo>

                        {allowed &&
                            <Flex>
                                {activeUser.type === 'user' && permissionGroupHelper.hasMinimumPermissionGroup(activeUser.permissionGroupId, 'member') &&
                                    <LinkButton
                                        variant={"clean"}
                                        href={applicationEndpoints.dashboard}
                                        icon="arrows_arrow_arrowBack"
                                        text="AMO portalen"
                                    />
                                }
                                <LinkButton
                                    variant={"clean"}
                                    href={"/portal"}
                                    icon="navigation_home"
                                    text="Oversigten"
                                />

                                <LinkButton
                                    variant={"clean"}
                                    href={applicationEndpoints.portal_logout}
                                    icon="logout"
                                    text="Log ud"
                                />
                            </Flex>
                        }
                    </Wrapper>
                </PublicNavigation>
                <CoreContent className="mainContent">

                    {allowed && props.children}

                    {!allowed &&
                        <SingleColumnLayout
                            noDivider
                            headerContainer={
                                <PageHeader headerText="Hov" headerStyle='primary-black' />
                            }
                            contentContainer={
                                <P>Hov! Du er havnet på en side, som du ikke har adgang til.</P>
                            }
                        />
                    }

                </CoreContent>
                <CoreFooter>
                    <Footer />
                </CoreFooter>
            </Main>
        </EmployeePortalPageProvider>
    );
};

const Main = styled.div`
    display:grid;
    min-height: 100%;
    grid-template-columns: auto;
    grid-template-rows: 100px auto min-content;
    grid-template-areas:
        "navigation"
        "content"
        "footer";

    @media (max-width: ${desktop}px) {
        grid-template-columns: auto;
        grid-template-rows: 65px auto min-content;
        grid-template-areas:
        "navigation"
        "content"
        "footer";
    }
`;

const CoreContent = styled.div`
    background: white;
    padding-bottom:20px;
    position: relative;
    z-index: 10;
    grid-area: content;
`;

const CoreFooter = styled.div`
    grid-area: footer;
`;

const PublicNavigation = styled.div`
    background: rgb(222, 215, 199);
    align-self: start;
    height: 100px;
    width: 100%;
    margin:0 auto;
    box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    -webkit-box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    -moz-box-shadow: 0px 8px 5px 0px rgba(0,0,0,0.25);
    z-index:1000;
  
    @media (max-width: ${desktop}px) {
        height: 50px;
        width: 50px;
    }
`;


const Wrapper = styled.div<{ wide?: boolean; }>`
    width:100%;
    margin: auto;
    max-width: ${props => props.wide ? '90%' : '1020px'};
    padding:16px 0px;
    justify-content: space-between;
    align-items: center;
    height: 100%;
    display: flex;
    flex-direction: row;
    padding: 0 10px;
`;


const PublicLogo = styled.div`
    display: flex;
    align-items: center;
    justify-content: start;
    height: 50%;
    width: 50%;

    & img {
        height: 100%;
        width: auto;
        max-width: 100%;
    }
`;